import dayjs from 'dayjs';
import { useState } from 'react';
import { ModalProps } from '../../../../../../../components/modal/modal';
import { tzOffset } from '../../../../../../../helpers/date-time';
import { validate } from '../../../../../../../helpers/validate';
import { useLang } from '../../../../../../../hooks/use-lang';
import { useQueryParams } from '../../../../../../../hooks/use-query-params';
import { PutShiftRequest, ShiftType } from '../../../../../../../store/api/shifts-api';
import { useShiftsStore } from '../../../../../../../store/use-shifts-store';

export const useShiftsGridEmployeeModal = (props: ModalProps) => {
  const { t, getLocale } = useLang();

  const params = useQueryParams().getQueryParams();
  const { crudShift, isLoading } = useShiftsStore();
  const shifts: ShiftType[] = JSON.parse(params.shifts);
  const date = params.date;
  const employee = params.employee ? JSON.parse(params.employee) : null;
  const employeeName = `${getLocale(employee?.name)}  ${getLocale(employee?.surname)}`;

  const [updatedShifts, setUpdatedShifts] = useState<
    Partial<PutShiftRequest & { from: string; to: string }>[]
  >(
    shifts.map((shift) => ({
      ...shift,
      action: 'update',
      from: dayjs(shift.start_time).format('HH:mm'),
      to: dayjs(shift.end_time).format('HH:mm'),
    })),
  );

  const handleSave = async () => {
    try {
      const fields = updatedShifts.flatMap((_, index) => [`from-${index}`, `to-${index}`]);
      const isValid = fields
        .map((field) =>
          validate({
            [field]: {
              rule: (val) => val.length === 5,
            },
          }),
        )
        .every(Boolean);
      if (!isValid) return;

      const format = 'YYYY-MM-DD HH:mm:ss';

      const doubleOffset = tzOffset * 2;

      const mapped = updatedShifts
        .filter((shift) => !(String(shift.id)?.startsWith('temp') && shift.action === 'delete'))
        .map((shift) => {
          const formattedDate = dayjs(shift.start_time).format('YYYY-MM-DD'); // без разницы на какую дату смотреть, это для сравнения начала/завершения смены и определения ночная она или нет
          const startDate = dayjs(`${formattedDate}T${shift?.from}:00.000Z`);
          const endDate = dayjs(`${formattedDate}T${shift?.to}:00.000Z`);

          const isNightShift = startDate.isAfter(endDate);

          let start_time = '';
          let end_time = '';

          if (isNightShift) {
            end_time = endDate.add(1, 'day').add(doubleOffset, 'minutes').format(format);
            start_time = startDate.add(doubleOffset, 'minutes').format(format);
          } else {
            start_time = startDate.add(doubleOffset, 'minutes').format(format);
            end_time = endDate.add(doubleOffset, 'minutes').format(format);
          }
          return { ...shift, start_time, end_time, staff: employee };
        });
      await crudShift(mapped);
      props.onClose();
    } catch (error) {
      console.error(error);
    }
  };

  const selectedDate = dayjs(date).format('YYYY-MM-DD');

  const handleAdd = () => {
    const updated = [
      ...updatedShifts,
      {
        action: 'create',
        from: '09:00',
        to: '18:00',
        id: `temp-${crypto.randomUUID()}`,
        start_time: selectedDate,
        end_time: selectedDate,
      },
    ];
    setUpdatedShifts(updated);
  };

  const handleDelete = (id: string) => {
    const updated = updatedShifts.map((item) =>
      String(item.id) === String(id) ? { ...item, action: 'delete' } : item,
    );
    setUpdatedShifts(updated);
  };

  const handleUpdate = (id: string, key: 'from' | 'to', value: string) => {
    const updated = updatedShifts.map((item) =>
      String(item.id) === String(id) ? { ...item, [key]: value } : item,
    );
    setUpdatedShifts(updated);
  };

  const label = dayjs(date).format('dddd, DD MMM');

  return {
    t,
    employeeName,
    handleDelete,
    handleSave,
    isLoading,
    handleAdd,
    updatedShifts: updatedShifts.filter((shift) => shift.action !== 'delete'),
    handleUpdate,
    label,
  };
};
